import { ChangeDetectorRef, Component, computed, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { LoggerService } from "@bequeathed/ngx-app-core";
import {AnalyticsService, InterviewState, MessageService} from "@bequeathed/ngx-services";
import { CookieService } from 'ngx-cookie-service';
import { BaseControl } from "../control/base-control/base-control.component";

const LOG: LoggerService = LoggerService.get('BaseFormComponent');

/**
 * Generic base UI component for form input validation and styling
 */
@Component({
    standalone: true,
    selector: 'base-form',
    templateUrl: './base-form.component.html',
    styleUrls: ['./base-form.component.sass']
})
export class BaseForm implements OnInit {

    messages = inject(MessageService)
    cookieService = inject(CookieService)
    changeDetector = inject(ChangeDetectorRef)
    analytics: AnalyticsService = inject(AnalyticsService)

    type = 'WILL';
    loading = false;
    preventSave = false;
    submitted = false;

    // keeps up-to-date input validation state
    errorMap = new Map<string, ErrorInfo[]>();
    // keeps copy of input errors when Submit is pressed and displayed above the form
    errorList = new Map<string, ErrorInfo[]>();

    // @ts-expect-error this will be set
    form: UntypedFormGroup;
    // @ts-expect-error this will be set
    formName: string;

    interviewState: WritableSignal<InterviewState | null> = signal(null);
    questionControl: BaseControl | undefined;
    questionControls: BaseControl[] = [];

    /**
     * Mid-interview backward-navigation edit mode.
     *
     * When the user confirms a jump back to an earlier, completed, non-immutable
     * topic (see the `interview-backward-navigation` spec), the interface enters
     * "editing an earlier answer" mode. The mode is tracked here — alongside the
     * `interviewState` signal, per the design decision to avoid NgRx — so that both
     * consumers can read it without a separate service:
     *   - `ChatInterfaceComponent` uses it to pick the edit endpoint (task 8).
     *   - the live-region banner uses it to announce the edit (task 9).
     *
     * `editTopicId` holds the `Topic.id` being edited while in edit mode; it is
     * `null` whenever edit mode is off. Use {@link enterEditMode} / {@link exitEditMode}
     * to transition — do not set the signals directly, so the two stay consistent.
     */
    private readonly _editingEarlierAnswer: WritableSignal<boolean> = signal(false);
    private readonly _editTopicId: WritableSignal<string | null> = signal(null);

    /**
     * The "latest" (current / outstanding) topic id the user was on before jumping
     * back to edit an earlier answer — i.e. the point they should be able to return
     * to WITHOUT making any change (see the `interview-backward-navigation` spec,
     * "return to latest").
     *
     * Captured by `ChatInterfaceComponent.onJumpToTopic` from
     * `navigator.currentTopic` before entering edit mode (when NOT already editing,
     * that is the latest outstanding topic, not yet completed). Passed into
     * {@link enterEditMode} so the navigator can offer it as a navigable return
     * target while editing. `null` whenever edit mode is off.
     */
    private readonly _latestTopicId: WritableSignal<string | null> = signal(null);

    /** Read-only view: is the user currently editing an earlier answer? */
    readonly editingEarlierAnswer: Signal<boolean> = this._editingEarlierAnswer.asReadonly();

    /** Read-only view: the `Topic.id` being edited, or `null` when not in edit mode. */
    readonly editTopicId: Signal<string | null> = this._editTopicId.asReadonly();

    /**
     * Read-only view: the latest / return-to topic id captured when the current
     * edit began, or `null` when not in edit mode.
     */
    readonly latestTopicId: Signal<string | null> = this._latestTopicId.asReadonly();

    /**
     * Convenience computed used by the endpoint selection (task 8): the target
     * topic id when in edit mode, otherwise `null`. Kept as a computed so callers
     * can depend on a single signal rather than combining the two above.
     */
    readonly activeEditTopicId: Signal<string | null> = computed(() =>
        this._editingEarlierAnswer() ? this._editTopicId() : null
    );

    protected redirectCookieName = 'beq_redirect';
    protected redirectUri = '/dashboard';

    /**
     * Initializes only a message bundle
     */
    ngOnInit(): void {
        this.interviewState = signal<InterviewState | null>(null)
        // Start every interview session in the normal forward flow, never mid-edit.
        // this.exitEditMode();
        const redirectUriCookie: string = this.cookieService.get(this.redirectCookieName);
        if (redirectUriCookie) {
            this.redirectUri = redirectUriCookie.replace(/[']+/g, '');
            LOG.debug('ngOnInit', `Redirect [${this.redirectUri}]`);
        }

        // MB-801 : when switching components, reset focus to the top page element
        document.getElementById('main-logo')?.focus();
        document.getElementById('main-logo')?.blur();
    }

    /**
     * Enter "editing an earlier answer" mode for the given topic.
     *
     * Called once the user has confirmed a jump back (task 8 wires this to the
     * confirmed `jumpToTopic`). Records the target `Topic.id` and flips the mode
     * on so the banner (task 9) and endpoint selection (task 8) can react.
     *
     * @param editTopicId the `Topic.id` the user is returning to. Must be a
     *                    non-empty id; a missing/blank id is ignored and leaves
     *                    the interview in the normal forward flow.
     * @param latestTopicId the "latest" / current outstanding topic id captured
     *                    before jumping back, so the navigator can offer a "return
     *                    to latest" target while editing. `null`/omitted when not
     *                    known.
     */
    enterEditMode(editTopicId: string, latestTopicId?: string | null): void {
        if (!editTopicId) {
            LOG.warn('enterEditMode called without a topic id; ignoring');
            return;
        }
        this._editTopicId.set(editTopicId);
        this._latestTopicId.set(latestTopicId ?? null);
        this._editingEarlierAnswer.set(true);
        LOG.debug('enterEditMode', `Editing earlier answer for topic [${editTopicId}] (latest [${latestTopicId ?? 'none'}])`);
    }

    /**
     * Exit edit mode and return to the normal forward flow.
     *
     * Called when the edit completes and the user is returned to the latest point
     * in the interview (task 8), or to reset state on (re)initialisation. Clears
     * the target topic id so {@link activeEditTopicId} reads `null` again.
     */
    exitEditMode(): void {
        if (this._editingEarlierAnswer()) {
            LOG.debug('exitEditMode', `Leaving edit mode for topic [${this._editTopicId()}]`);
        }
        this._editingEarlierAnswer.set(false);
        this._editTopicId.set(null);
        this._latestTopicId.set(null);
    }

    /** True while the user is editing an earlier answer. Convenience for templates. */
    isEditingEarlierAnswer(): boolean {
        return this._editingEarlierAnswer();
    }

    toggleSave(preventSave: boolean): void {
        this.preventSave = preventSave;
        this.changeDetector.detectChanges();
        LOG.debug('toggleSave', `Preventing save? ${preventSave}`);
    }

    toggleLoading(loading: boolean): void {
        this.loading = loading;
        this.changeDetector.detectChanges();
        LOG.debug('toggleLoading', `Loading? ${loading}`);
    }

    /**
     * Updates the validation error list above the form
     * @param form A formgroup instance representing main or sub-form on the current page
     * (base-form-component). By default it uses main FormGroup from the base-form instance
     * @param cleanErrors Boolean value to indicate if the errorMap and errorList collections should be cleared
     */
    setErrorList(form: UntypedFormGroup = this.form, cleanErrors = true): void {
        LOG.trace('setErrorList', 'Reset errors ...');
        if (cleanErrors) {
            this.errorMap.clear();
            this.errorList.clear();
        }
        LOG.trace('setErrorList', `... got controls [${!!form.controls}]`);
        if (form.controls) {
            form.markAllAsTouched();
            for (const controlName of Object.keys(form.controls)) {
                if (form.controls[controlName] instanceof UntypedFormGroup) {
                    this.setErrorList(form.controls[controlName] as UntypedFormGroup, false);
                } else {
                    if (!this.revalidateControl(controlName, form) && this.errorMap.size) {
                        this.errorList.set(controlName, this.errorMap.get(controlName)!);
                    }
                }
            }
        }
    }

    /**
     * Creates an intermediate object for detailed input's error behavior
     */
    createErrorInfo(err: string, controlName: string): ErrorInfo {
        return { field: controlName, error: err } as ErrorInfo;
    }

    /**
     * Revalidates the form upon submit
     */
    async revalidateForm(): Promise<void> {
        // Clear previous errors
        this.errorMap.clear();
        this.form.updateValueAndValidity();

        const errored: BaseControl[] = [];
        // @ts-expect-error this will be fine
        const errors: ErrorInfo[] = await this.questionControl.validate();
        if (errors?.length) {
            errored.push(this.questionControl!);
            this.errorMap.set(this.interviewState()!.currentQuestion!.id!, errors);
            LOG.warn(`Form contains errors: ${JSON.stringify(errors)}`);
        }

        if (errored.length > 0) {
            LOG.warn(`Form contains ${errored.length} errors!`);
            errored[0].focus();
        }
    }

    /**
     * Revalidates the whole form
     * @param focusList true if should set focus on the list above the form
     * @param form A formgroup instance representing main or sub-form on the current page (base-form-component). By default it
     *             uses main FormGroup from the base-form instance
     */
    async revalidate(focusList?: boolean, form: UntypedFormGroup = this.form): Promise<boolean> {
        if (form.invalid) {
            LOG.trace('onSubmit', `... form valid ? [${!!form.valid}] ... set errors ...`);
            this.setErrorList(form);
            await this.revalidateForm();
            // this.revalidateForm(focusList, form);
            return true;
        }
        return false;
    }

    /**
     * Single control helper validity function used to determine if error message/block should be visible
     *
     * @param controlName
     * @param form A formgroup instance representing main or sub-form on the current page (base-form-component). By default it
     *             uses main FormGroup from the base-form instance
     */
    feedbackHasError(controlName: string, form: UntypedFormGroup = this.form): boolean {
        return form.controls[controlName].touched || this.isSubmitted(form);
    }

    /**
     * Revalidates single control
     *
     * @param controlName name of the control field
     * @param form A formgroup instance representing main or sub-form on the current page (base-form-component). By default it
     *             uses main FormGroup from the base-form instance
     */
    revalidateControl(controlName: string, form: UntypedFormGroup = this.form) {
        const control: AbstractControl = form.controls[controlName];
        this.errorMap.delete(controlName);
        LOG.debug('revalidateControl', `validation pending? ${control?.pending}`);
        if (!!control && !control.disabled && !control.valid) {
            LOG.debug('revalidateControl', `... controls [${controlName}] valid [${(control.valid)}]`);

            const errors: ErrorInfo[] = [];
            form.controls[controlName].updateValueAndValidity();
            if (control.errors) {
                for (const err of Object.keys(control.errors)) {
                    errors.push(this.createErrorInfo(err, controlName));
                }
            }

            this.errorMap.set(controlName, errors);
        }

        return control?.valid;
    }

    /**
     * Sets a list of errors on the given control.
     *
     * @param control, the AbstractControl
     * @param errors, the list of errors to add to the control
     */
    setFieldErrors(control: AbstractControl, errors: ErrorInfo[]): void {
        LOG.debug('setFieldErrors', `Is valid [${!!control.valid}] with [${control.errors != null ? (Object.keys(control.errors)).length : 0}] errors`);
        const fieldErrors = {};
        errors.forEach((e: any) => {
            // @ts-expect-error this will be fine
            fieldErrors[e.error] = e.message;
        });

        control.setErrors(fieldErrors);
    }

    /**
     * Shortcut for revalidateControl used in the template
     * @param $inputName
     * @param form A formgroup instance representing main or sub-form on the current page
     * (base-form-component). By default it uses main FormGroup from the base-form instance
     */
    revalidateInput($inputName: any, form: UntypedFormGroup = this.form) {
        this.revalidateControl($inputName.target.id, form);
    }

    /**
     * Retrieves error message for given control and validation error name
     * @param err
     * @param formName
     */

    getValidationMessage(err: ErrorInfo, formName: string): string | null {
        return err ? this.messages.getText(`message.error.${formName}.${err.field}.${err.error}`) : null;
    }

    /**
     * Sorted, constant list of invalid controls, needed to avoid concurrent modification in the template while submitting
     */
    getInvalidInputNames(): string[] {
        return Array.from(this.errorList.keys());
    }

    isSubmitted(form: UntypedFormGroup = this.form): boolean {
        // @ts-expect-error this will be fine
        return this.submitted || (form['submitted'] === true);
    }

    applyErrors(id: string, errors: ValidationErrors): void {
        if (!this.errorMap.has(id)) {
            this.errorMap.set(id, []);
        }

        if (!errors) {
            return;
        }

        const errs: ErrorInfo[] = this.errorMap.get(id)!;
        Object.keys(errors)
              .filter((err: any) => errs.filter((e: ErrorInfo) => e.field === id && e.error === err).length === 0)
              .map((key: string) => {
                  return {
                      field: id,
                      error: key
                  } as ErrorInfo;
              })
              .forEach((err: any) => errs.push(err));

    }
}

/**
 * Detailed error information and message
 */
export class ErrorInfo {
    field?: string;
    error?: string;
    message?: string;
    value?: string;
    lines?: string[];
}
